'use strict';

/* globals Gryphon, page_state */

import { toPayloadValue } from '../../utils/lib/store-payload';

// Cap on how many variable names ride along on one event; `mismatchCount` is
// always exact. A 40-row grid should not push 2 KB into New Relic.
var MAX_REPORTED_NAMES = 20;

// Canonical class order (RPI-1032). Reporting sorts by this so
// `mismatchClasses` is deterministic — `payload-empty,value-differs` and
// `value-differs,payload-empty` no longer fall into two separate NRQL groups.
// Every class `classify` can return appears here exactly once.
var CLASS_ORDER = [
  'store-missing',
  'store-empty',
  'payload-empty',
  'value-differs',
  'whitespace-only',
  'on-vs-code',
];

// Class -> the count field it reports under. All six always report a count
// (0 where the class did not occur) so an NRQL `SELECT sum(...)` is reliable.
var COUNT_FIELD = {
  'store-missing': 'storeMissingCount',
  'store-empty': 'storeEmptyCount',
  'payload-empty': 'payloadEmptyCount',
  'value-differs': 'valueDiffersCount',
  'whitespace-only': 'whitespaceOnlyCount',
  'on-vs-code': 'onVsCodeCount',
};

// Class -> the per-class variable-list field, for the four classes that name a
// genuine coverage/sync gap worth drilling into. A `*Vars` field is emitted
// only when that class occurred, so empty lists do not clutter the event.
var VARS_FIELD = {
  'store-missing': 'storeMissingVars',
  'store-empty': 'storeEmptyVars',
  'payload-empty': 'payloadEmptyVars',
  'value-differs': 'valueDiffersVars',
};

// Set `window.__shadowDebug = true` in the console to trace this locally.
function debugLog() {
  if (window.__shadowDebug && window.console) {
    console.log.apply(
      console,
      ['[shadow-compare]'].concat([].slice.call(arguments))
    );
  }
}

// A throw in here used to disappear into `catch (e) {}`. Report it once per
// page: a broken comparison must not look like a passing one.
var failureReportedFor = null;

function reportCompareFailure(e) {
  debugLog('THREW', e);
  try {
    if (failureReportedFor === page_state.this_page) {
      return;
    }
    failureReportedFor = page_state.this_page;
    if (typeof window.newrelic !== 'undefined') {
      window.newrelic.noticeError(
        new Error('AnswerStore/shadow compare failed'),
        {
          errorMessage: 'AnswerStore/shadow compare failed',
          page: page_state.this_page,
          shadowCompare: true,
          cause: (e && e.message) || String(e),
        }
      );
    }
  } catch (nested) {}
}

function joinCapped(names) {
  if (names.length <= MAX_REPORTED_NAMES) {
    return names.join(',');
  }
  return names.slice(0, MAX_REPORTED_NAMES).join(',') + ',…';
}

function unique(values) {
  var seen = {};
  var out = [];
  for (var i = 0; i < values.length; i++) {
    if (!seen[values[i]]) {
      seen[values[i]] = true;
      out.push(values[i]);
    }
  }
  return out;
}

/**
 * Last path segment of each script URL, for QSL attribution.
 * @param {Array<string>} urls
 * @returns {Array<string>}
 */
function basenames(urls) {
  var out = [];
  for (var i = 0; i < urls.length; i++) {
    out.push(String(urls[i]).split('?')[0].split('/').pop());
  }
  return out;
}

/**
 * Name the shape of one difference, so triage is a GROUP BY rather than a
 * person reading a dashboard. RR-4 reported only the widget type, which is why
 * five distinct causes were indistinguishable in the data.
 *
 * @param {string} payload The value submit is sending.
 * @param {string} shadow The value the store would submit.
 * @param {*} raw The store's value before conversion.
 * @returns {string}
 */
function classify(payload, shadow, raw) {
  if (raw === undefined) {
    // A genuine coverage gap: a native write, a late-rendered input, or an
    // answer channel that bypasses the form.
    return 'store-missing';
  }
  if (payload === '') {
    return 'payload-empty';
  }
  if (shadow === '') {
    return 'store-empty';
  }
  if (payload === 'on' || shadow === 'on') {
    // One side is the DOM's `'on'` for a value-less checkbox and the other is
    // the response code — i.e. the seed is not in payload encoding.
    return 'on-vs-code';
  }
  if (payload.trim() === shadow.trim()) {
    return 'whitespace-only';
  }
  return 'value-differs';
}

/**
 * Compare the payload the store would produce against the one being submitted.
 * @param {Object} realData The payload built from the DOM, fully assembled.
 */
export function compareSubmitPayload(realData) {
  try {
    // Local tracing only. These rows carry answer VALUES, so they exist purely
    // for `console.table` on a developer's machine and are never attached to
    // anything sent to New Relic.
    var debug = !!window.__shadowDebug;

    debugLog('called');

    // New Relic is not loaded in a dev build, so without `debug` this would do
    // nothing locally — which is exactly where it needs to be inspectable.
    if (typeof window.newrelic === 'undefined' && !debug) {
      debugLog('skipped: no window.newrelic');
      return;
    }

    // Read each declared variable individually (RPI-1031): the store-derived
    // payload is defined by `allvars`, not by the store's key set, and RR-5 will
    // read the same way. No `getAll()`.
    var store = Gryphon && Gryphon.answers ? Gryphon.answers : null;
    var allvars = page_state.allvars || [];
    var vardata = page_state.vardata || {};
    var hasOwn = Object.prototype.hasOwnProperty;

    var names = [];
    // class -> [variable names], in `allvars` order. The overall `names` list
    // is kept alongside it for the back-compatible aggregate fields.
    var byClass = Object.create(null);
    var rows = [];

    for (var i = 0; i < allvars.length; i++) {
      var name = allvars[i];

      // Submit overwrites these from `page_state.vardata` (`events.js`), and
      // RR-5 keeps that merge, so both routes carry the same bytes by
      // construction. Not RR-5 risk, not measured here.
      var isVardata = hasOwn.call(vardata, name);
      if (isVardata && !debug) {
        continue;
      }

      var payload = toPayloadValue(
        hasOwn.call(realData, name) ? realData[name] : ''
      );
      var raw = store ? store.get(name) : undefined;
      var shadow = toPayloadValue(raw);
      var verdict;

      if (isVardata) {
        verdict = 'skip: vardata';
      } else if (shadow === payload) {
        verdict = 'ok';
      } else {
        verdict = classify(payload, shadow, raw);
        names.push(name);
        (byClass[verdict] || (byClass[verdict] = [])).push(name);
      }

      if (debug) {
        rows.push({
          variable: name,
          'store holds': JSON.stringify(raw),
          'store would submit': JSON.stringify(shadow),
          'DOM submits': JSON.stringify(payload),
          verdict: verdict,
        });
      }
    }

    if (debug) {
      debugLog('page', page_state.this_page, '- full payload:', realData);
      if (console.table) {
        console.table(rows);
      } else {
        debugLog(rows);
      }
      debugLog(
        'checked',
        allvars.length,
        'vars -',
        names.length,
        'would change under RR-5',
        names
      );
    }

    if (names.length === 0) {
      return;
    }

    if (typeof window.newrelic === 'undefined') {
      debugLog('not reported: no window.newrelic (local build)');
      return;
    }

    var message = 'AnswerStore/DOM submit payload mismatch';

    // Variable names are reported; answer values never are.
    //
    // The seeding counters ride along on this event rather than getting one of
    // their own. `page_state.unseeded_variables` is otherwise only visible in a
    // local build: `.env` is the only file setting NODE_ENV=development, and
    // every `.env.staging.*` / `.env.production.*` sets it to production.
    //
    // Counts, not name lists: `joinCapped` truncates at MAX_REPORTED_NAMES, so
    // a name list would be cut off on exactly the pages carrying the most debt
    // (a 20x7 grid has ~140 variables). `renderables[].view` buckets it by
    // widget type instead, which is the unit follow-up tickets are written in.
    //
    // `qslCount` is a count and not a boolean on purpose: a shared library that
    // ships on every survey alongside the per-questionnaire script would make a
    // boolean uniformly true and partition nothing. Read the distribution
    // first, then derive the partition from it.
    var qsl = page_state._js || [];
    var event = {
      errorMessage: message,
      page: page_state.this_page,
      shadowCompare: true,
      mismatchCount: names.length,
      mismatchedVars: joinCapped(names),
      unseededCount: (page_state.unseeded_variables || []).length,
      strayCount: (page_state.stray_seeded_variables || []).length,
      seededViews: unique(
        (page_state.renderables || []).map(function (r) {
          return (r && r.view) || '';
        })
      ).join(','),
      qslCount: qsl.length,
      qslScripts: joinCapped(basenames(qsl)),
    };

    // Per-class breakdown (RPI-1032). Walking CLASS_ORDER makes `mismatchClasses`
    // deterministic and every count present (0 where the class did not occur).
    // A `*Vars` list rides along only for the four coverage/sync classes and
    // only when non-empty. Variable names and counts only — never values.
    var presentClasses = [];
    for (var c = 0; c < CLASS_ORDER.length; c++) {
      var cls = CLASS_ORDER[c];
      var classVars = byClass[cls] || [];
      event[COUNT_FIELD[cls]] = classVars.length;
      if (classVars.length) {
        presentClasses.push(cls);
        if (VARS_FIELD[cls]) {
          event[VARS_FIELD[cls]] = joinCapped(classVars);
        }
      }
    }
    event.mismatchClasses = presentClasses.join(',');

    window.newrelic.noticeError(new Error(message), event);
  } catch (e) {
    reportCompareFailure(e);
  }
}
